import { Alert, Group, Paper, SimpleGrid, Skeleton, Stack, Text, Title } from '@mantine/core';
import { IconAlertCircle, IconBookmark } from '@tabler/icons-react';
import { ButtonLink, Delayed, DirectoryCard, IllustrativeBadge } from '~portal/components/common';
import type { LiveQuery } from '~portal/data';
import type { TrackedCard } from '~portal/data/home';
import { sizedLogoUrl } from '~portal/data/operator-directory';
import { subjectRoute } from './links';

/**
 * @desc Tracked pages (H6, Figma `426:7806`): the operators and regions the user follows as a
 * 3×2 grid of the shared directory card — logo or flag, a group-or-continent eyebrow (F-3:
 * consistent), the page's own name, a description where the page has one and the arrow; hover is the card's
 * own (H8). Live from `follows.list` and the region pages (any page the region route renders: markets,
 * states, the roll-up, continents), so no badge; "Page not found" marks only a follow no page exists for. Empty (H7), it
 * becomes the call to action that explains how pages get here and offers both listings to start
 * from.
 */
export function TrackedPages({ tracked }: { tracked: LiveQuery<readonly TrackedCard[]> }) {
  return (
    <Stack gap="sm" component="section" aria-labelledby="home-tracked">
      <Title order={2} id="home-tracked">
        Your tracked pages
      </Title>
      {tracked.isLoading && (
        <Delayed>
          <SimpleGrid
            cols={{ base: 1, sm: 2, lg: 3 }}
            spacing="lg"
            data-testid="home-tracked-loading"
          >
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} height={150} radius="lg" />
            ))}
          </SimpleGrid>
        </Delayed>
      )}
      {tracked.error && (
        <Alert
          variant="light"
          color="red"
          icon={<IconAlertCircle size={18} />}
          title="Tracked pages could not be loaded"
        >
          {tracked.error.message}
        </Alert>
      )}
      {tracked.data && tracked.data.length === 0 && <TrackedEmpty />}
      {tracked.data && tracked.data.length > 0 && (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          {tracked.data.map((card) => (
            <DirectoryCard
              key={`${card.type}:${card.slug}`}
              to={subjectRoute({ kind: card.type })}
              params={{ slug: card.slug }}
              logo={{
                name: card.name,
                ...(card.logoSlug ? { logoSlug: card.logoSlug } : {}),
                ...(card.logoUrl ? { src: sizedLogoUrl(card.logoUrl, 40) } : {}),
                ...(card.flagCode ? { flagCode: card.flagCode } : {}),
                ...(card.code ? { code: card.code } : {}),
              }}
              eyebrow={card.eyebrow}
              name={card.name}
              description={card.description}
              badge={card.unresolved ? <IllustrativeBadge label="Page not found" /> : undefined}
            />
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
}

/** @desc The empty state (H7, frame `433:8510`): what the section is for, and where to start. */
function TrackedEmpty() {
  return (
    <Paper withBorder p="xl" data-testid="home-tracked-empty">
      <Stack align="center" gap="sm">
        <IconBookmark size={28} color="var(--mantine-color-dimmed)" aria-hidden />
        <Text c="dimmed" ta="center">
          Follow an operator or region to see it here
        </Text>
        <Group gap="sm" justify="center">
          <ButtonLink to="/operators" variant="default">
            Browse operators
          </ButtonLink>
          <ButtonLink to="/regions" variant="default">
            Browse regions
          </ButtonLink>
        </Group>
      </Stack>
    </Paper>
  );
}
